import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "../../hooks/useToastContext";
import AuditLogTable from "./AuditLogTable";
import AuditLogView from "./AuditLogView";
import { auditLogActions, auditLogResults, auditLogRoles } from "../../utils/auditLogs";

const AuditLogs = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState({ sortBy: "createdAt", sortType: "desc" });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedResult, setSelectedResult] = useState("");
  const [viewModal, setViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const getAllAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit,
        search,
        sortBy: sort.sortBy,
        sortType: sort.sortType
      });

      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (selectedRole) params.append("role", selectedRole);
      if (selectedAction) params.append("action", selectedAction);
      if (selectedResult) params.append("result", selectedResult);

      const result = await apiGet(`${apiPath.auditLogs}?${params.toString()}`);
      
      // FIX: Handle both response structures
      const responseData = result?.data?.results || result?.data?.data;
      
      if (result?.data?.success && responseData) {
        setLogs(responseData.logs || []);
        setTotal(responseData.total || 0);
        setTotalPages(responseData.totalPages || 1);
      } else {
        notification.error(result?.data?.message || "Error fetching logs");
      }
    } catch (error) {
      console.log("Error in getting audit logs:", error);
      notification.error(error.message);
    }
    setLoading(false);
  }, [page, limit, search, sort, startDate, endDate, selectedRole, selectedAction, selectedResult]);

  useEffect(() => {
    getAllAuditLogs();
  }, [getAllAuditLogs]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setSelectedRole("");
    setSelectedAction("");
    setSelectedResult("");
    setPage(1);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setViewModal(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {loading && (
            <div className="spinner-container">
              <div className="loading-spinner"></div>
            </div>
          )}
        </div>
      </div>

      {/* Filters Section - 2 Rows Only */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        
        {/* Row 1: Search + Start Date + End Date + All Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search Input */}
          <div className="relative">
            <div className="relative">
              <input
                type="text"
                className="block w-full px-4 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500 peer"
                placeholder={t("SEARCH_LOGS")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <button
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                onClick={handleSearch}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Start Date */}
          <div className="relative">
            <input
              type="date"
              className="block w-full px-3 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <label className="absolute -top-2 left-3 px-2 bg-white text-xs text-gray-500">
              {t("START_DATE")}
            </label>
          </div>

          {/* End Date */}
          <div className="relative">
            <input
              type="date"
              className="block w-full px-3 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <label className="absolute -top-2 left-3 px-2 bg-white text-xs text-gray-500">
              {t("END_DATE")}
            </label>
          </div>

          {/* Action Filter - Human readable labels */}
          <select
            className="block w-full px-3 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
            value={selectedAction}
            onChange={(e) => {
              setSelectedAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Actions</option>
            {auditLogActions.map((action) => (
              <option key={action.value} value={action.value}>
                {action.label}
              </option>
            ))}
          </select>
        </div>

        {/* Row 2: All Roles + All Results + Reset Button */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Role Filter - Human readable labels */}
          <select
            className="block w-full px-3 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
            value={selectedRole}
            onChange={(e) => {
              setSelectedRole(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Roles</option>
            {auditLogRoles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>

          {/* Result Filter - Human readable labels */}
          <select
            className="block w-full px-3 py-2.5 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
            value={selectedResult}
            onChange={(e) => {
              setSelectedResult(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Results</option>
            {auditLogResults.map((result) => (
              <option key={result.value} value={result.value}>
                {result.label}
              </option>
            ))}
          </select>

          {/* Reset Button - Aligned with filters */}
          <div className="flex justify-end items-center">
            <button
              className="text-black bg-[#E1E1E1] font-normal px-6 py-2.5 text-sm outline-none focus:outline-none rounded mr-3 ease-linear transition-all duration-150"
              onClick={handleReset}
            >
              {t("RESET")}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <AuditLogTable
          logs={logs}
          page={page}
          sort={sort}
          setSort={setSort}
          handleView={handleView}
        />

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t("SHOW")}</span>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={limit}
                onChange={handleLimitChange}
              >
                <option key={10} value={10}>10</option>
                <option key={25} value={25}>25</option>
                <option key={50} value={50}>50</option>
                <option key={100} value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">{t("ENTRIES")}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {total} {t("TOTAL_RECORDS")}
              </span>
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              >
                {t("PREVIOUS")}
              </button>
              <span className="text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                {t("NEXT")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewModal && selectedItem && (
        <AuditLogView item={selectedItem} handleView={() => setViewModal(false)} />
      )}
    </div>
  );
};

export default AuditLogs;